import React, { FC, useEffect, useState } from 'react';
import { Menu } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Divider, Spin, SuccessCard } from 'common/components';
import { AuthHooks, BasketHooks, UserHooks } from 'common/hooks';
import { showSuccessMessage } from 'common/helpers';
import { ERoute, ESocketEvent } from 'common/const';
import { useSocketContext } from 'common/context';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { GoodAnalogues, GoodGallery, GoodGeneralInformation, GoodProperties, GoodSidebar } from 'entities/Goods/components';
import { getCurrentBasket, selectCurrentBasket, selectIsBasketLoading, updateBasket } from 'entities/Basket/Basket.slice';
import {
  getGoodById,
  getGoodSettings,
  sendRequestForGoodApproval,
  updateGood,
  patchGoodSettingsItemIsProgress,
  selectGood,
  selectIsGoodLoading,
  selectGoodSettings,
} from 'entities/Goods/Good.slice';
import { getUserStats, selectCurrentUser } from 'entities/User/User.slice';
import { IBasket, IUpdateBasketPayload } from 'entities/Basket/Basket.models';
import { IUpdateGoodPayload } from 'entities/Goods/Goods.models';
import { selectXBasketId } from 'entities/Auth/Auth.slice';

export const GoodPage: FC = () => {
  const dispatch = useAppDispatch();

  const xBasketId = useAppSelector(selectXBasketId);
  const currentBasket = useAppSelector(selectCurrentBasket);
  const isBasketLoading = useAppSelector(selectIsBasketLoading);
  const good = useAppSelector(selectGood);
  const isGoodLoading = useAppSelector(selectIsGoodLoading);
  const goodSettings = useAppSelector(selectGoodSettings);
  const currentUser = useAppSelector(selectCurrentUser);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [menuSelectedKeys, setMenuSelectedKeys] = useState<string[]>(['analogues']);
  const [openSuccessCard, setOpenSuccessCard] = useState<boolean>(false);
  const { catalogId, goodId } = useParams<{ catalogId: string; goodId: string }>();
  const navigate = useNavigate();
  const { isSeller } = AuthHooks.useIsSeller();
  const basketId = BasketHooks.useCurrentBasketId();
  const { isAccountAdmin, isSellerManager } = UserHooks.useCurrentUserRole();
  const { socket } = useSocketContext();
  const [searchParams] = useSearchParams();

  const isManuallyAdded = searchParams.get('isManuallyAdded');
  const isCustomer = !isSeller || xBasketId;

  const fetchData = async (): Promise<void> => {
    if (!goodId) {
      return;
    }

    if (basketId) {
      await dispatch(getCurrentBasket(basketId));
    }

    if (!isCustomer && (isAccountAdmin || isSellerManager)) {
      await dispatch(getGoodSettings({ goodId: Number(goodId) }));
    }

    const good = await dispatch(
      getGoodById({
        id: Number(goodId),
        ...(catalogId && { catalogId: Number(catalogId) }),
        ...(isManuallyAdded !== null && { isManuallyAdded: isManuallyAdded === 'true' }),
      }),
    );

    if (good && !good.analogGoods?.length) {
      setMenuSelectedKeys(['description']);
    }

    setFetchLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [goodId, isManuallyAdded, currentUser?.city?.id]);

  useEffect(() => {
    const updateSyncInProgress = (value: any) => {
      dispatch(patchGoodSettingsItemIsProgress(value));
    };

    socket?.on(ESocketEvent.ContractSync, updateSyncInProgress);

    return () => {
      socket?.off(ESocketEvent.ContractSync, updateSyncInProgress);
    };
  }, [socket]);

  const changeMenuKey = (key: string): void => {
    setMenuSelectedKeys([key]);

    const element = document.getElementById(key);

    if (!element) {
      return;
    }

    element.scrollIntoView({ behavior: 'smooth' });
  };

  const addGoodToBasket = (id: number, count: number): void => {
    if (currentBasket) {
      const newBasketGoods = [
        ...currentBasket.goods.map((item) => ({ goodId: item.goodId, count: item.count })),
        { goodId: id, count },
      ];

      dispatch(
        updateBasket({
          id: currentBasket.id,
          goods: newBasketGoods,
          onSuccess: async () => {
            showSuccessMessage('Товар добавлен в корзину.');
            dispatch(getUserStats());
          },
        }),
      );
    }
  };

  const navigateToBasket = (): void => {
    if (xBasketId) {
      navigate(`/active-basket-list/${xBasketId}`);
    } else {
      navigate(ERoute.Basket);
    }
  };

  const sendRequest = async (): Promise<void> => {
    if (!good) {
      return;
    }

    await dispatch(
      sendRequestForGoodApproval({
        goodId: good.id,
        onSuccess: () => setOpenSuccessCard(true),
      }),
    );
  };

  const handleUpdateBasket = async (payload: IUpdateBasketPayload): Promise<IBasket | null> => dispatch(updateBasket(payload));

  const closeSuccessCard = (): void => setOpenSuccessCard(false);

  const handleUpdateGood = async (payload: IUpdateGoodPayload): Promise<void> => await dispatch(updateGood(payload));

  if (fetchLoading) {
    return (
      <div className="h-full flex align-center justify-center">
        <Spin />
      </div>
    );
  }

  return (
    <div className="redesign relative pb-9 flex align-start gap-11 good-page">
      <div className="pt-14 good-page-content">
        <div className="mb-11 flex align-start justify-between gap-11">
          <GoodGallery good={good} />

          <GoodGeneralInformation
            good={good}
            loading={isGoodLoading}
            onNavigateToProperties={changeMenuKey}
            updateGood={handleUpdateGood}
          />
        </div>

        <Menu
          className="mb-6"
          mode="horizontal"
          items={[
            ...(good?.analogGoods?.length ? [{ label: 'Аналогичные товары', key: 'analogues' }] : []),
            { label: 'Описание товара', key: 'description' },
            { label: 'Технические характеристики', key: 'properties' },
          ]}
          selectedKeys={menuSelectedKeys}
          onClick={({ key }) => changeMenuKey(key)}
        />

        <div id="analogues">
          <div className="mb-6">
            <GoodAnalogues
              analogues={good?.analogGoods}
              loading={isBasketLoading}
              onAddToBasketClick={addGoodToBasket}
              onNavigateToBasketClick={navigateToBasket}
            />
          </div>
        </div>

        <div id="description">
          <div className="mb-6 ff-medium fs-m">Описание товара</div>

          <div className="ff-regular fs-s">{good?.description}</div>
        </div>

        <Divider marginY={20} />

        <div id="properties">
          <div className="mb-6">
            <GoodProperties good={good} />
          </div>
        </div>
      </div>

      <div className="pt-14 good-page-sidebar">
        <GoodSidebar
          good={good}
          loading={isBasketLoading}
          settings={goodSettings}
          onAddToBasketClick={addGoodToBasket}
          onNavigateToBasketClick={navigateToBasket}
          onBasketUpdate={handleUpdateBasket}
          onSendRequestClick={sendRequest}
        />
      </div>

      <SuccessCard
        open={openSuccessCard}
        content="Ваш запрос о заказе позиции направлен поставщику. Он свяжется с вами напрямую."
        subtitle="Отправлено!"
        btnTitle="Назад"
        onClose={closeSuccessCard}
        onConfirm={closeSuccessCard}
      />
    </div>
  );
};
